// pages/DirezioneDashboard.js
// Dashboard Direzione V3 (SIMPLIFIED) — control room direzionale compatta.
// KPI direzionali INVARIATI; Controllo commerciale COMPATTO (3 indicatori + max 5 priorità)
// alimentato dal Customer Follow-up Engine condiviso (invariato lato motore).

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus } from 'lucide-react';
import { DashboardKpiCard } from '../components/dashboard/DashboardKpiCard';
import { ControlloCommerciale } from '../components/dashboard/ControlloCommerciale';
import { VerificheAnomalieBar } from '../components/dashboard/VerificheAnomalieBar';
import { CommercialPipelineV2 } from '../components/dashboard/CommercialPipelineV2';
import { RecentCommercialActivity } from '../components/dashboard/RecentCommercialActivity';
import { UpcomingAppointmentsV2 } from '../components/dashboard/UpcomingAppointmentsV2';

const DirezioneDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [controlExpanded, setControlExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await dashboardApi.getDirezione();
        setData(res.data);
      } catch (e) {
        console.error('Errore dashboard direzione:', e);
        toast.error('Errore nel caricamento della dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Errore nel caricamento della dashboard</p>
      </div>
    );
  }

  const {
    title, subtitle, kpis = [], attention = [], pipeline = [],
    pipeline_total_value = 0, pipeline_total_count = 0,
    recent_activity = [], upcoming_appointments = [],
    commercial_control = {},
  } = data;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="direzione-dashboard">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {title || `Benvenuto, ${user?.first_name}`}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        <Link to="/leads">
          <Button className="bg-[#FF7819] hover:bg-[#e06a15] text-white shadow-sm" data-testid="new-lead-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Lead
          </Button>
        </Link>
      </div>

      {/* ROW 1: TOP KPI DIREZIONALI V4 — Da fatturare · Da incassare · Valore pipeline · Cantieri attivi */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="direzione-kpi-grid">
        {kpis.map((kpi) => <DashboardKpiCard key={kpi.id} kpi={kpi} />)}
      </div>

      {/* ROW 2: CONTROLLO COMMERCIALE + PIPELINE COMMERCIALE — parità in standard,
          espansione indipendente del solo Controllo (§9-10). */}
      <div
        className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${controlExpanded ? 'lg:items-start' : 'lg:items-stretch'}`}
        data-testid="direzione-row-commercial"
      >
        <div className={controlExpanded ? '' : 'lg:h-full'}>
          <ControlloCommerciale control={commercial_control} onExpandChange={setControlExpanded} />
        </div>
        <div className={controlExpanded ? '' : 'lg:h-full'}>
          <CommercialPipelineV2 stages={pipeline} totalValue={pipeline_total_value} totalCount={pipeline_total_count} />
        </div>
      </div>

      {/* ROW 3: VERIFICHE E ANOMALIE — barra compatta full-width (§14-17) */}
      <VerificheAnomalieBar groups={attention} />

      {/* ROW 4: PROSSIMI APPUNTAMENTI (~40%) + ATTIVITÀ COMMERCIALE RECENTE (~60%) (§19-25) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:items-stretch" data-testid="direzione-row-operativa">
        <div className="lg:col-span-2 lg:h-full">
          <UpcomingAppointmentsV2 appointments={upcoming_appointments} />
        </div>
        <div className="lg:col-span-3 lg:h-full">
          <RecentCommercialActivity items={recent_activity} />
        </div>
      </div>
    </div>
  );
};

export default DirezioneDashboard;
